/*
 * Pure helpers shared by the section editors. No React, no DOM: everything
 * here is a value-in/value-out function so it can be unit tested directly.
 */

/* ---- repeated-row list editing ------------------------------------------ */

/** Move list[from] to index `to`. Returns the original list if either index is
    out of range, so the caller can wire up/down buttons without guarding. */
export function moveItem(list, from, to) {
  const items = Array.isArray(list) ? list : [];
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Drop list[index]. Out-of-range leaves the list alone. */
export function removeItem(list, index) {
  const items = Array.isArray(list) ? list : [];
  if (index < 0 || index >= items.length) return items;
  return items.slice(0, index).concat(items.slice(index + 1));
}

/** Replace list[index] with `value`. Out-of-range leaves the list alone. */
export function replaceItem(list, index, value) {
  const items = Array.isArray(list) ? list : [];
  if (index < 0 || index >= items.length) return items;
  const next = items.slice();
  next[index] = value;
  return next;
}

/** Merge `patch` into the object at list[index], keeping its other keys. */
export function patchItem(list, index, patch) {
  const items = Array.isArray(list) ? list : [];
  if (index < 0 || index >= items.length) return items;
  return replaceItem(items, index, { ...(items[index] || {}), ...patch });
}

/** Append `value` to the list. */
export function appendItem(list, value) {
  return (Array.isArray(list) ? list : []).concat([value]);
}

/* ---- dataset rows ------------------------------------------------------- */
/* Firestore cannot nest an array inside an array, so a dataset row is stored
   wrapped: {"cells": ["2021", "5,039,835"]}. The editor works in plain arrays
   of cells and wraps on the way back out, keeping any other keys a migration
   script put on the row. */

/** The cells of one stored row, whatever shape it arrived in. */
export function rowCells(row) {
  if (Array.isArray(row)) return row.map((c) => (c === null || c === undefined ? '' : c));
  if (row && Array.isArray(row.cells)) return row.cells.map((c) => (c === null || c === undefined ? '' : c));
  return [];
}

/** Stored rows -> arrays of cells, each padded to `width` columns. */
export function unwrapRows(rows, width) {
  return (Array.isArray(rows) ? rows : []).map((row) => padCells(rowCells(row), width));
}

/** Pad (or leave) a cell list so it is at least `width` long. */
export function padCells(cells, width) {
  const out = (cells || []).slice();
  const n = Number(width) || 0;
  while (out.length < n) out.push('');
  return out;
}

/** Arrays of cells -> stored rows, preserving extra keys on the old row at the
    same index (a migration script may have stashed something there). */
export function wrapRows(cellRows, previousRows) {
  const prev = Array.isArray(previousRows) ? previousRows : [];
  return (Array.isArray(cellRows) ? cellRows : []).map((cells, i) => {
    const before = prev[i];
    const extras = before && !Array.isArray(before) && typeof before === 'object' ? { ...before } : {};
    return { ...extras, cells: (cells || []).slice() };
  });
}

/* ---- CSV paste --------------------------------------------------------- */

/** Tab, semicolon or comma, whichever wins on the first line. */
export function guessDelimiter(text) {
  const line = String(text || '').split('\n')[0] || '';
  const count = (ch) => line.split(ch).length - 1;
  const tabs = count('\t');
  const semis = count(';');
  const commas = count(',');
  if (tabs >= semis && tabs >= commas && tabs > 0) return '\t';
  if (semis > commas) return ';';
  return ',';
}

/**
 * Parse pasted CSV/TSV into an array of cell arrays. Handles quoted fields
 * (with "" for a literal quote), CRLF, and ragged lines. Blank lines are
 * dropped, so a trailing newline does not produce an empty row.
 */
export function parseCsv(text, delimiter) {
  const src = String(text === null || text === undefined ? '' : text).replace(/\r\n?/g, '\n');
  if (!src.trim()) return [];
  const delim = delimiter || guessDelimiter(src);
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  let started = false; // has this field seen a non-quote character yet

  const endField = () => { row.push(field.trim()); field = ''; started = false; };
  const endRow = () => { endField(); rows.push(row); row = []; };

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else { quoted = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"' && !started) {
      quoted = true;
      started = true;
    } else if (ch === delim) {
      endField();
    } else if (ch === '\n') {
      endRow();
    } else {
      field += ch;
      started = true;
    }
  }
  endRow();
  return rows.filter((r) => r.some((c) => c !== ''));
}

/**
 * Parse pasted text into a dataset table. With `hasHeader`, the first row
 * becomes the column headers. Every row is padded to the column count so the
 * table editor stays rectangular.
 */
export function parseCsvTable(text, { hasHeader = true, columns = [] } = {}) {
  const parsed = parseCsv(text);
  if (!parsed.length) return { columns: columns.slice(), rows: [] };
  const header = hasHeader ? parsed[0].map((c) => String(c)) : columns.slice();
  const body = hasHeader ? parsed.slice(1) : parsed;
  const width = Math.max(header.length, ...body.map((r) => r.length), 1);
  return {
    columns: padCells(header, width).map((c) => String(c)),
    rows: body.map((r) => padCells(r, width)),
  };
}

/** "5,039,835" -> 5039835; "$1.2" -> 1.2; "n/a" -> "n/a". Labels stay strings. */
export function toNumberish(value) {
  if (typeof value === 'number') return value;
  const s = String(value === null || value === undefined ? '' : value).trim();
  if (!s) return '';
  const cleaned = s.replace(/[,$%\s]/g, '');
  if (cleaned && /^-?\d+(\.\d+)?$/.test(cleaned)) return Number(cleaned);
  return s;
}

/**
 * Chart data for a dataset: a header row first, then the table rows with their
 * figures unformatted, because the site's chart cannot parse "5,039,835".
 * The first cell of each row is the label and stays a string.
 */
export function buildToPlot(columns, cellRows) {
  const head = (columns || []).map((c) => String(c === null || c === undefined ? '' : c));
  const body = (Array.isArray(cellRows) ? cellRows : []).map((cells) =>
    (cells || []).map((c, i) => (i === 0 ? String(c === null || c === undefined ? '' : c) : toNumberish(c))));
  if (!head.length && !body.length) return [];
  return wrapRows([head].concat(body));
}
